import {
    Box,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { PeopleAlt, Search } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import { useIndicacoesAmigosContext } from "../context/useIndicacoesAmigosContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    semDados: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(4),
    },
    dataUtilizada: {
        color: "#4caf50",
        fontWeight: 500,
    },
    dataPendente: {
        color: "#7f8c8d",
    },
}))

function formatarData(timestamp: number | null): string {
    if (timestamp === null || timestamp === undefined) return "—"
    return new Date(timestamp).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

const normalizar = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

export function TabelaIndicacoes() {
    const classes = useStyles()
    const { dados, erroCarregamento } = useIndicacoesAmigosContext()
    const { enqueueSnackbar } = useSnackbar()
    const [termoBusca, setTermoBusca] = useState("")

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const listaFiltrada = termoBusca
        ? dados.indicacoes.filter(ind => {
              const nomeConvidante = ind.nomeConvidante ?? ""
              const nomeConvidado = ind.nomeConvidado ?? ""
              const termo = normalizar(termoBusca)
              return (
                  normalizar(nomeConvidante).includes(termo) ||
                  normalizar(nomeConvidado).includes(termo)
              )
          })
        : dados.indicacoes

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText} gutterBottom>
                    Indicações de Amigos
                </Typography>

                <Box className={classes.configCard} component={Paper}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            <PeopleAlt className={classes.iconHeader} />
                            Lista de Indicações
                        </Typography>
                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                            {dados.indicacoes.length} indicação(ões)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box mb={2}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Buscar por nome do convidante ou convidado..."
                                value={termoBusca}
                                onChange={e => setTermoBusca(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search style={{ color: "#7f8c8d", fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ width: "100%", minWidth: 560 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 160 }}>
                                            <strong>Convidante</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 160 }}>
                                            <strong>Convidado</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 120 }}>
                                            <strong>Data de Cadastro</strong>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 120 }}>
                                            <strong>Data de Utilização</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {listaFiltrada.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className={classes.semDados}>
                                                {termoBusca
                                                    ? "Nenhuma indicação encontrada para esta busca."
                                                    : "Nenhuma indicação registrada."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        listaFiltrada.map(ind => (
                                            <TableRow key={ind.id} hover>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={ind.nomeConvidante ?? ""}
                                                >
                                                    {ind.nomeConvidante ?? "—"}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={ind.nomeConvidado ?? ""}
                                                >
                                                    {ind.nomeConvidado ?? "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {formatarData(ind.dataCadastro)}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        ind.dataUtilizacao !== null
                                                            ? classes.dataUtilizada
                                                            : classes.dataPendente
                                                    }
                                                >
                                                    {ind.dataUtilizacao !== null
                                                        ? formatarData(ind.dataUtilizacao)
                                                        : "Pendente"}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
